<!-- src/components/dashboard/BankingSummaryCard.vue (CONTENT ONLY) -->
<template>
  <div class="banking-summary-content">
    <div
      v-if="error"
      class="card-panel red lighten-5 red-text text-darken-3"
      style="margin-top: 12px"
    >
      {{ error }}
    </div>

    <div v-if="loading" class="progress" style="margin-top: 12px">
      <div class="indeterminate"></div>
    </div>

    <div class="responsive-table banking-summary-table-wrap">
      <table class="striped">
        <thead>
          <tr>
            <th></th>
            <th v-for="d in dayCols" :key="d.key" class="right-align">
              {{ d.label }}
            </th>
            <th class="right-align">Total</th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td><strong>Total PDQ</strong></td>
            <td v-for="d in dayCols" :key="d.key" class="right-align">
              {{ gbp(dayValue(d.key).pdq) }}
            </td>
            <td class="right-align">
              <strong>{{ gbp(totals.pdq) }}</strong>
            </td>
          </tr>

          <tr>
            <td><strong>Cash</strong></td>
            <td v-for="d in dayCols" :key="d.key" class="right-align">
              {{ gbp(dayValue(d.key).cash) }}
            </td>
            <td class="right-align">
              <strong>{{ gbp(totals.cash) }}</strong>
            </td>
          </tr>

          <tr>
            <td><strong>Total</strong></td>
            <td v-for="d in dayCols" :key="d.key" class="right-align">
              <strong>{{ gbp(dayValue(d.key).pdq + dayValue(d.key).cash) }}</strong>
            </td>
            <td class="right-align">
              <strong>{{ gbp(totals.pdq + totals.cash) }}</strong>
            </td>
          </tr>

          <tr v-if="!hasAnyData && !loading">
            <td colspan="9" class="grey-text">No tills submitted for this week.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { getTillsForDateRange } from "@/scripts/sqlApiHelper/tillsApi";
import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

const props = defineProps({
  venueId: { type: String, required: true },
  weekEndingDate: { type: String, required: true },
});

const { setBankingSummaryPayload } = useVenueContext();

const loading = ref(false);
const error = ref("");
const byDate = ref({});

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function gbp(n) {
  const v = Math.round(num(n) * 100) / 100;
  return v.toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseYmd(ymd) {
  const [y, m, d] = String(ymd).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function toYmd(dateObj) {
  const y = dateObj.getUTCFullYear();
  const m = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function addDays(dateObj, n) {
  const d = new Date(dateObj.getTime());
  d.setUTCDate(d.getUTCDate() + n);
  return d;
}

const weekDates = computed(() => {
  const we = parseYmd(props.weekEndingDate);
  const mon = addDays(we, -6);
  return Array.from({ length: 7 }, (_, i) => toYmd(addDays(mon, i)));
});

const dayCols = computed(() => [
  { key: weekDates.value[0], label: "Monday" },
  { key: weekDates.value[1], label: "Tuesday" },
  { key: weekDates.value[2], label: "Wednesday" },
  { key: weekDates.value[3], label: "Thursday" },
  { key: weekDates.value[4], label: "Friday" },
  { key: weekDates.value[5], label: "Saturday" },
  { key: weekDates.value[6], label: "Sunday" },
]);

function dayValue(ymd) {
  const v = byDate.value?.[ymd];
  return {
    pdq: num(v?.pdq),
    cash: num(v?.cash),
  };
}

const totals = computed(() => {
  let pdq = 0;
  let cash = 0;

  for (const ymd of weekDates.value) {
    const v = dayValue(ymd);
    pdq += v.pdq;
    cash += v.cash;
  }

  return { pdq, cash };
});

const hasAnyData = computed(() => {
  return weekDates.value.some((ymd) => {
    const v = dayValue(ymd);
    return v.pdq !== 0 || v.cash !== 0;
  });
});

const bankingSummaryPayload = computed(() => {
  const pdq = weekDates.value.map((ymd) => dayValue(ymd).pdq);
  const cash = weekDates.value.map((ymd) => dayValue(ymd).cash);
  const total = weekDates.value.map((ymd) => dayValue(ymd).pdq + dayValue(ymd).cash);

  return { pdq, cash, total };
});

function extractLines(row) {
  if (!row || typeof row !== "object") return [];
  if (Array.isArray(row.lines)) return row.lines;
  if (Array.isArray(row.till_count_lines)) return row.till_count_lines;
  return [];
}

function extractTradingDate(row) {
  const td = row?.trading_date ?? row?.tradingDate ?? row?.date ?? row?.trading_day;

  return td ? String(td).slice(0, 10) : "";
}

async function load() {
  if (!props.venueId || !props.weekEndingDate) return;

  loading.value = true;
  error.value = "";

  try {
    const res = await getTillsForDateRange({
      venue_id: props.venueId,
      date_from: weekDates.value[0],
      date_to: weekDates.value[6],
    });

    const rows = Array.isArray(res?.rows) ? res.rows : [];

    const seeded = {};

    for (const ymd of weekDates.value) {
      seeded[ymd] = {
        pdq: 0,
        cash: 0,
        _pdq: 0,
        _cash: 0,
        _float: 0,
      };
    }

    for (const r of rows) {
      const ymd = extractTradingDate(r);

      if (!seeded[ymd]) continue;

      for (const ln of extractLines(r)) {
        const type = String(ln?.line_type).toLowerCase();
        const amt = num(ln?.amount);

        if (type === "pdq_total") {
          seeded[ymd]._pdq_total = (seeded[ymd]._pdq_total || 0) + amt;
        } else if (type === "pdq") {
          seeded[ymd]._pdq_raw = (seeded[ymd]._pdq_raw || 0) + amt;
        }

        if (type === "cash_total") {
          seeded[ymd]._cash_total = (seeded[ymd]._cash_total || 0) + amt;
        } else if (type === "cash") {
          seeded[ymd]._cash_raw = (seeded[ymd]._cash_raw || 0) + amt;
        }

        if (type === "float") {
          seeded[ymd]._float = (seeded[ymd]._float || 0) + amt;
        }
      }
    }

    for (const ymd of weekDates.value) {
      const row = seeded[ymd];

      row.pdq = num(row._pdq_total ?? row._pdq_raw ?? 0);
      row.cash = num(row._cash_total ?? row._cash_raw ?? 0) - num(row._float ?? 0);

      delete row._pdq_total;
      delete row._pdq_raw;
      delete row._cash_total;
      delete row._cash_raw;
      delete row._float;
    }

    byDate.value = seeded;

    setBankingSummaryPayload(bankingSummaryPayload.value);
  } catch (e) {
    error.value = e?.message || "Failed to load banking summary.";
    setBankingSummaryPayload(null);
  } finally {
    loading.value = false;
  }
}

defineExpose({
  totals,
  bankingSummaryPayload,
  refresh: load,
});

onMounted(load);

watch(() => [props.venueId, props.weekEndingDate], load);
</script>

<style scoped>
.banking-summary-content {
  width: 100%;
}

@media (max-width: 600px) {
  .banking-summary-table-wrap table {
    min-width: 820px;
  }
}
</style>
